import { BrowserRouter } from "react-router-dom";
import Routes from "./pages/shared/Routes.tsx";
import { Application } from "./pages/shared/Application";
import { AuthenticationProvider } from "./pages/shared/AuthenticationContext";
import { SocketProvider } from "./pages/shared/SocketContext";
import { NotificationProvider } from "./pages/shared/NotificationProvider";
import { BrowserNotificationSocketBridge } from "./pages/shared/BrowserNotificationSocketBridge.tsx";
import { NotificationPermissionPrompt } from "./pages/shared/NotificationPermissionPrompt.tsx";
import { BrowserNotificationClickHandler } from "./pages/shared/BrowserNotificationClickHandler.tsx";
import { ModalContainer } from "./pages/shared/bootstrap/modal/Modal";
import { ToastProvider } from "./pages/shared/bootstrap/Toast";
import { NavigationHistoryProvider } from "./pages/shared/NavigationHistory";
import { FloatingPanel } from "./pages/shared/FloatingPanel";
import { CounterMsgProvider } from "./pages/shared/CounterMsgContext";
import { HelmetProvider } from "react-helmet-async";
import "./utils/ArrayExtensionMethods";
import MaintenancePage from "./pages/shared/MaintenancePage.tsx";
import MaintenanceContext from "./pages/shared/MaintenanceContext.tsx";
import { useMaintenanceMode } from "./hooks/useMaintenanceMode.ts";
import { TranslationService } from "./services/TranslationService.ts";

const SUPPORTED_LANGS = ["es", "en", "pt"];

function detectLang() {
  const pathLang = window.location.pathname.split("/")[1];
  return SUPPORTED_LANGS.includes(pathLang) ? pathLang : "es";
}

function App() {
  const { loading, endTime, es, en, pt } = useMaintenanceMode();

  // PROVISORIO: hardcodeamos flags de mantenimiento en false mientras no podamos traer la config del archivo.
  const maintenanceForced = false;
  const showBannerForced = false;

  // PROVISORIO: textos forzados (replica de `public/maintenance.json`) para no depender del fetch.
  const endTimeForced = "16:00 (UTC-0)";
  const forcedTextsByLang = {
    es: {
      title: "Estamos en mantenimiento",
      description:
        "El sistema se encuentra temporalmente fuera de servicio por tareas de mantenimiento programado. Volvemos pronto.",
      estimatedEnd: "Tiempo estimado de finalización:",
      banner:
        "¡Atención! El domingo 12-Abr-26 entre las 11:00 y las 16:00 (UTC-0) Intiza Cobranzas estará en mantenimiento."
    },
    en: {
      title: "We're under maintenance",
      description:
        "The system is temporarily out of service due to scheduled maintenance. We'll be back soon.",
      estimatedEnd: "Estimated completion time:",
      banner:
        "Attention! On Sunday, Apr-12-26, between 11:00 and 16:00 (UTC-0), Intiza Cobranzas will be undergoing maintenance."
    },
    pt: {
      title: "Estamos em manutenção",
      description:
        "O sistema está temporariamente fora de serviço devido a manutenção programada. Voltamos em breve.",
      estimatedEnd: "Tempo estimado de conclusão:",
      banner:
        "Atenção! No domingo, 12-Abr-26, entre as 11:00 e as 16:00 (UTC-0), o Intiza Cobranzas estará em manutenção."
    }
  };

  if (loading) {
    return null;
  }

  const lang = detectLang();
  TranslationService.setCurrentLanguage(lang);
  const textsFromHook = { es, en, pt }[lang] ?? es;
  const texts = maintenanceForced
    ? (forcedTextsByLang[lang] ?? forcedTextsByLang.es)
    : textsFromHook;
  const effectiveEndTime = maintenanceForced ? endTimeForced : endTime;
  const bannerText = showBannerForced
    ? TranslationService.translate.MaintenanceBanner
    : texts.banner;

  if (maintenanceForced) {
    return (
      <MaintenancePage
        title={texts.title}
        description={texts.description}
        estimatedEnd={texts.estimatedEnd}
        endTime={effectiveEndTime}
      />
    );
  }

  return (
    <MaintenanceContext.Provider
      value={{ showBanner: showBannerForced, bannerText }}
    >
      <HelmetProvider>
        <Application>
          <AuthenticationProvider>
            <CounterMsgProvider>
              <SocketProvider>
                <NotificationProvider>
                  <BrowserNotificationSocketBridge />
                  <NotificationPermissionPrompt />
                  <ToastProvider>
                    <BrowserRouter>
                      <NavigationHistoryProvider>
                        <BrowserNotificationClickHandler />
                        <Routes />
                        <FloatingPanel
                          id="floating-window"
                          height={200}
                          width={400}
                          resizable={true}
                          titleBar={{
                            title: "Intiza CRM",
                            buttons: {
                              minimize: true,
                              maximize: false,
                              close: false
                            }
                          }}
                        />
                        <ModalContainer />
                      </NavigationHistoryProvider>
                    </BrowserRouter>
                  </ToastProvider>
                </NotificationProvider>
              </SocketProvider>
            </CounterMsgProvider>
          </AuthenticationProvider>
        </Application>
      </HelmetProvider>
    </MaintenanceContext.Provider>
  );
}

export default App;
